// Form validation utilities for Savidna CRM

export interface ValidationResult {
  isValid: boolean
  errors: Record<string, string>
}

export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

export const validatePhone = (phone: string): boolean => {
  const phoneRegex = /^[\d\s\-\+\(\)]{7,20}$/
  return phoneRegex.test(phone)
}

export const validateRequired = (
  value: string | number | null | undefined,
): boolean => {
  if (value === null || value === undefined) return false
  if (typeof value === 'string') return value.trim().length > 0
  return true
}

export const validateMinLength = (
  value: string,
  minLength: number,
): boolean => {
  return value.length >= minLength
}

export const validateMaxLength = (
  value: string,
  maxLength: number,
): boolean => {
  return value.length <= maxLength
}

export const validatePositiveNumber = (value: number): boolean => {
  return value > 0
}

export const validateAge = (age: number): boolean => {
  return age >= 0 && age <= 150
}

export const validateTime = (time: string): boolean => {
  const timeRegex = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/
  return timeRegex.test(time)
}

export const validateDate = (date: string): boolean => {
  const dateObj = new Date(date)
  return !isNaN(dateObj.getTime())
}

export const validateFutureDate = (date: string): boolean => {
  const dateObj = new Date(date)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return dateObj >= today
}

export const validatePassword = (
  password: string,
): { isValid: boolean; message: string } => {
  if (password.length < 6) {
    return {
      isValid: false,
      message: 'La contraseña debe tener al menos 6 caracteres',
    }
  }
  return { isValid: true, message: '' }
}

export const validateLoginForm = (
  email: string,
  password: string,
): ValidationResult => {
  const errors: Record<string, string> = {}

  if (!validateRequired(email)) {
    errors.email = 'El correo electrónico es requerido'
  } else if (!validateEmail(email)) {
    errors.email = 'El correo electrónico no es válido'
  }

  if (!validateRequired(password)) {
    errors.password = 'La contraseña es requerida'
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  }
}

export const validateRegisterForm = (
  name: string,
  email: string,
  password: string,
  confirmPassword: string,
): ValidationResult => {
  const errors: Record<string, string> = {}

  if (!validateRequired(name)) {
    errors.name = 'El nombre es requerido'
  } else if (!validateMinLength(name, 2)) {
    errors.name = 'El nombre debe tener al menos 2 caracteres'
  }

  if (!validateRequired(email)) {
    errors.email = 'El correo electrónico es requerido'
  } else if (!validateEmail(email)) {
    errors.email = 'El correo electrónico no es válido'
  }

  const passwordValidation = validatePassword(password)
  if (!validateRequired(password)) {
    errors.password = 'La contraseña es requerida'
  } else if (!passwordValidation.isValid) {
    errors.password = passwordValidation.message
  }

  if (password !== confirmPassword) {
    errors.confirmPassword = 'Las contraseñas no coinciden'
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  }
}

export const validatePatientForm = (data: {
  name: string
  age: number
  gender: string
  phone: string
  email: string
  address: string
}): ValidationResult => {
  const errors: Record<string, string> = {}

  if (!validateRequired(data.name)) {
    errors.name = 'El nombre es requerido'
  }

  if (!validateAge(data.age)) {
    errors.age = 'La edad debe estar entre 0 y 150'
  }

  if (!validateRequired(data.gender)) {
    errors.gender = 'El sexo es requerido'
  }

  if (!validateRequired(data.phone)) {
    errors.phone = 'El teléfono es requerido'
  } else if (!validatePhone(data.phone)) {
    errors.phone = 'El teléfono no es válido'
  }

  if (data.email && !validateEmail(data.email)) {
    errors.email = 'El correo electrónico no es válido'
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  }
}

export const validateAppointmentForm = (data: {
  patientId: string
  doctorId: string
  serviceId: string
  date: string
  time: string
}): ValidationResult => {
  const errors: Record<string, string> = {}

  if (!validateRequired(data.patientId)) {
    errors.patientId = 'Debe seleccionar un paciente'
  }

  if (!validateRequired(data.doctorId)) {
    errors.doctorId = 'Debe seleccionar un doctor'
  }

  if (!validateRequired(data.serviceId)) {
    errors.serviceId = 'Debe seleccionar un servicio'
  }

  if (!validateRequired(data.date)) {
    errors.date = 'La fecha es requerida'
  } else if (!validateFutureDate(data.date)) {
    errors.date = 'La fecha debe ser hoy o en el futuro'
  }

  if (!validateRequired(data.time)) {
    errors.time = 'La hora es requerida'
  } else if (!validateTime(data.time)) {
    errors.time = 'La hora no es válida'
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  }
}
